/**
 * PrayerModule — full Prayer page (route: #/prayer).
 *
 * Scope: prayer-time data ONLY. No adhkar, Quran, hadith, fasting calendar
 * or other Islamic content (those live in the Memoriz app). The boundary is
 * documented in CLAUDE.md § "Yawmi vs Memoriz Canonical Scope Boundary".
 *
 * UX:
 *   1. City picker (current + watched, reuses WatchedCitiesService)
 *   2. Hero: next prayer + Hijri date + city + source indicator
 *   3. Today: 6 prayers (passed/active/upcoming) + iqama + tahajjud window
 *   4. Sun-arc visualization (Fajr → ʿIshāʾ progression)
 *   5. Qibla compass (degree + DeviceOrientationEvent live needle)
 *   6. 30-day calendar (Aladhan /calendar one-shot, IDB-cached)
 *   7. Settings: 23 methods, madhab, tune, iqama, high-lat, midnight, shafaq
 *
 * Data: PrayerTimeService (server cache + Adhan.js fallback) +
 * WeatherService.geocodeCity for the picker.
 */

import type { AweniContext, AweniModule } from '../../types';
import type { WatchedCity } from '../home/types';
import { LocationService } from '../../services/LocationService';
import { WeatherService, type GeocodeResult } from '../../services/WeatherService';
import { getWatchedCitiesService, type WatchedCitiesService } from '../../services/WatchedCitiesService';
import {
  prayerTimeService,
  METHOD_DISPLAY,
  type PrayerTimesData,
  type CalculationMethodKey,
  type MadhabKey,
  type HighLatitudeRuleKey,
  type MidnightModeKey,
  type ShafaqKey,
  type PrayerTuneConfig,
  type IqamaDelaysConfig,
} from '../../services/PrayerTimeService';
import { gregorianToHijri } from '../../services/HijriDate';
import { SourceAttributionStrip } from '../../shell/SourceAttributionStrip';

interface ActiveLocation {
  id: string; // 'current' or watched-city id
  lat: number;
  lng: number;
  name: string;
  isCurrent: boolean;
  /** IANA tz id (e.g. "Australia/Sydney"). Required for non-current cities
   *  so prayer times render in THEIR clock, not the device's. Undefined
   *  for the current device location (formatTime falls back to device tz,
   *  which IS the device's local — correct). */
  tz?: string;
}

type PrayerKey = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';
const PRAYER_ORDER: PrayerKey[] = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];
const IQAMA_KEYS: Array<keyof IqamaDelaysConfig> = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

export default class PrayerModule implements AweniModule {
  private container: HTMLElement | null = null;
  private ctx: AweniContext | null = null;
  /** SourceAttributionStrip in the prayer hero. Recreated on each
   *  renderHero() so settings changes (method/madhab) are reflected. */
  private attrStrip: SourceAttributionStrip | null = null;
  private locationService = new LocationService();
  private weatherService = new WeatherService(this.locationService);
  private watched: WatchedCitiesService = getWatchedCitiesService();

  private active: ActiveLocation | null = null;
  private activeData: PrayerTimesData | null = null;
  private monthDays: Array<Record<string, unknown>> | null = null;

  private settingsOpen = false;
  private searchOpen = false;
  private searchResults: GeocodeResult[] = [];
  private searching = false;
  private searchTimer: number | null = null;

  private compassEnabled = false;
  private compassHeading = 0;
  private compassListener: ((e: DeviceOrientationEvent) => void) | null = null;

  private countdownCleanup: (() => void) | null = null;
  private serviceUnsub: (() => void) | null = null;
  private unsubWatched: (() => void) | null = null;

  async mount(container: HTMLElement, context: AweniContext): Promise<void> {
    this.container = container;
    this.ctx = context;
    container.style.cssText = 'min-height:100%;background:var(--ln-bg-primary, var(--bg-primary));';

    // Pick initial active: LocationService → PrayerTimeService fallback →
    // first watched city. The two services have independent storage
    // (aweni_location vs aweni_prayer_location); if the user granted
    // location through the Prayer entry point only, mirror it back so the
    // module + the Home hero see the same state.
    let loc = this.locationService.getState();
    if (loc.lat === null || loc.lng === null) {
      const prayerLoc = prayerTimeService.getLocation();
      if (prayerLoc && Number.isFinite(prayerLoc.lat) && Number.isFinite(prayerLoc.lng)) {
        this.locationService.setManualLocation(
          prayerLoc.lat,
          prayerLoc.lng,
          prayerLoc.city || context.i18n.t('prayer_current_location'),
        );
        loc = this.locationService.getState();
      }
    }

    if (loc.lat !== null && loc.lng !== null) {
      this.active = {
        id: 'current',
        lat: loc.lat,
        lng: loc.lng,
        name: loc.city || context.i18n.t('prayer_current_location'),
        isCurrent: true,
      };
    } else {
      const first = this.watched.list()[0];
      if (first) this.active = { id: first.id, lat: first.lat, lng: first.lng, name: first.name, isCurrent: false, tz: first.timeZone };
    }

    // Hydrate activeData synchronously from cache, render, then revalidate.
    this.refreshActiveData();
    this.render();

    // Background tasks (after first paint, per "Home anti-regression rules")
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => void this.loadMonthCalendar());
    } else {
      setTimeout(() => void this.loadMonthCalendar(), 200);
    }

    // Subscribe to service changes (settings updates, etc.) and watched-city changes.
    this.serviceUnsub = prayerTimeService.onChange(() => {
      this.refreshActiveData();
      this.render();
    });
    this.unsubWatched = this.watched.onChange(() => this.render());

    // Live countdown for the hero
    this.countdownCleanup = prayerTimeService.startCountdown(() => {
      const heroCountdown = this.container?.querySelector('[data-region="hero-countdown"]') as HTMLElement | null;
      if (heroCountdown && this.activeData?.nextPrayer) {
        heroCountdown.textContent = this.formatCountdown(
          Math.max(0, Math.floor((this.activeData.nextPrayer.time.getTime() - Date.now()) / 1000)),
        );
      }
    });
  }

  unmount(): void {
    if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null; }
    this.countdownCleanup?.(); this.countdownCleanup = null;
    this.serviceUnsub?.(); this.serviceUnsub = null;
    this.unsubWatched?.(); this.unsubWatched = null;
    this.attrStrip?.unmount(); this.attrStrip = null;
    this.stopCompass();
    this.container = null;
    this.ctx = null;
  }

  // ---------- data ----------

  private refreshActiveData(): void {
    if (!this.active) { this.activeData = null; return; }
    if (this.active.isCurrent) {
      // Use the global service (which has server-cached times for the active device location)
      this.activeData = prayerTimeService.getTodayTimes() ?? prayerTimeService.getTimesForCoords(this.active.lat, this.active.lng);
    } else {
      // Non-current city: compute locally via Adhan.js with current settings.
      this.activeData = prayerTimeService.getTimesForCoords(this.active.lat, this.active.lng);
    }
  }

  private async loadMonthCalendar(): Promise<void> {
    if (!this.active) return;
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;

    // For the current device location we can reuse the service's server-fetch
    // pipeline. For watched cities, we'd need to expose a coords-aware variant —
    // for now, only fetch the calendar when the active location is the current
    // device location (since the service's location is what the calendar uses).
    if (!this.active.isCurrent) return;

    // Fast path: peek IDB cache → render instantly
    const cached = await prayerTimeService.peekCalendar(year, month);
    if (cached) {
      this.monthDays = cached;
      this.renderCalendar();
    }

    const fresh = await prayerTimeService.fetchCalendar(year, month);
    if (fresh) {
      this.monthDays = fresh;
      this.renderCalendar();
    }
  }

  private setActive(loc: ActiveLocation): void {
    this.active = loc;
    this.refreshActiveData();
    this.monthDays = null;
    this.render();
    if (loc.isCurrent) void this.loadMonthCalendar();
    // Self-heal: if a watched city is missing its IANA timezone (legacy
    // data added before tz capture was wired), fetch it once via Open-Meteo
    // geocoding and persist on the WatchedCity record so the next pick is
    // already correct. Without this, prayer times for Sydney render in the
    // device's tz (= wrong by 9–11h for a Casablanca user).
    if (!loc.isCurrent && !loc.tz && loc.id !== 'current') {
      void this.healCityTz(loc);
    }
  }

  /** Fetch a city's IANA timezone via Open-Meteo geocoding and persist it.
   *  Best-effort — if the lookup fails, prayer times keep rendering in the
   *  device tz (still wrong, but at least nothing crashes). */
  private async healCityTz(loc: ActiveLocation): Promise<void> {
    try {
      const lang = this.ctx?.i18n.locale ?? 'en';
      const results = await this.weatherService.geocodeCity(loc.name, lang);
      // Match by closest coords (~10 km) to avoid grabbing a same-named
      // city in another country.
      const match = results.find((r) =>
        Math.abs(r.latitude - loc.lat) < 0.1 && Math.abs(r.longitude - loc.lng) < 0.1
      ) || results[0];
      if (!match || !match.timezone) return;
      // Mutate the watched city so future picks already have tz.
      const all = this.watched.list();
      const target = all.find((c) => c.id === loc.id);
      if (target) {
        // WatchedCitiesService doesn't expose a direct update method —
        // emulate via remove + re-add at same position. Skip if id matches
        // a non-watched entry (e.g., 'current').
        const idx = all.findIndex((c) => c.id === loc.id);
        const order = all.map((c) => c.id);
        this.watched.remove(loc.id);
        const added = this.watched.add({
          name: target.name,
          countryCode: target.countryCode,
          admin1: target.admin1,
          lat: target.lat,
          lng: target.lng,
          timeZone: match.timezone,
        });
        // Best-effort restore order
        if (added) {
          order[idx] = added.id;
          this.watched.reorder(order);
        }
        // Update active in-memory and re-render with the now-correct tz.
        this.active = { ...loc, id: added?.id ?? loc.id, tz: match.timezone };
        this.render();
      }
    } catch { /* silent — keeps the previous render */ }
  }

  // ---------- top-level render ----------

  private render(): void {
    if (!this.container || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    if (!this.active) {
      this.container.innerHTML = `
        <div style="padding:32px 20px;text-align:center;">
          <div style="font-size:44px;margin-bottom:12px;">\u{1F54C}</div>
          <h2 style="font-size:18px;font-weight:700;color:var(--ln-text-primary, var(--text-primary));margin-bottom:8px;">
            ${esc(t('prayer_title'))}
          </h2>
          <p style="font-size:14px;color:var(--ln-text-secondary, var(--text-secondary));margin-bottom:20px;">
            ${esc(t('prayer_no_location'))}
          </p>
          <button data-act="search-toggle"
            style="padding:10px 20px;border-radius:12px;background:var(--ln-accent, var(--accent));color:var(--ln-accent-text, var(--accent-text));
                   font-weight:600;border:none;cursor:pointer;">
            ${esc(t('prayer_add_city'))}
          </button>
          <div data-region="search" style="margin-top:16px;text-align:start;"></div>
        </div>
      `;
      this.attachHandlers();
      this.renderSearch();
      return;
    }

    this.container.innerHTML = `
      <div style="max-width:560px;margin:0 auto;padding:12px 16px 32px;">
        <div data-region="picker"></div>
        <div data-region="hero"></div>
        <div data-region="today"></div>
        <div data-region="sun-arc"></div>
        <div data-region="qibla"></div>
        <div data-region="calendar"></div>
        <div data-region="settings"></div>
        <div data-region="search" style="margin-top:14px;"></div>
      </div>
    `;
    this.renderPicker();
    this.renderHero();
    this.renderTodayList();
    this.renderSunArc();
    this.renderQibla();
    this.renderCalendar();
    this.renderSettings();
    this.renderSearch();
    this.attachHandlers();
  }

  // ---------- regions ----------

  private renderPicker(): void {
    const host = this.container?.querySelector('[data-region="picker"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.active) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    const loc = this.locationService.getState();
    const items: ActiveLocation[] = [];
    if (loc.lat !== null && loc.lng !== null) {
      items.push({
        id: 'current',
        lat: loc.lat,
        lng: loc.lng,
        name: loc.city || t('prayer_current_location'),
        isCurrent: true,
      });
    }
    for (const c of this.watched.list()) {
      items.push({ id: c.id, lat: c.lat, lng: c.lng, name: c.name, isCurrent: false });
    }

    const chips = items
      .map((item) => {
        const isActive = item.id === this.active!.id;
        const removeBtn =
          !item.isCurrent && isActive
            ? `<button data-act="remove-city" data-id="${escAttr(item.id)}"
                aria-label="${escAttr(t('prayer_remove_city'))}"
                style="margin-inline-start:4px;background:none;border:none;color:inherit;
                       cursor:pointer;font-size:14px;line-height:1;padding:0 2px;">×</button>`
            : '';
        const pin = item.isCurrent ? '\u{1F4CD} ' : '';
        return `
          <button data-act="pick-city" data-id="${escAttr(item.id)}"
            style="display:inline-flex;align-items:center;gap:4px;padding:6px 12px;
                   border-radius:999px;border:1px solid ${isActive ? 'var(--ln-accent, var(--accent))' : 'var(--ln-border, var(--border))'};
                   background:${isActive ? 'var(--ln-accent, var(--accent))' : 'var(--ln-bg-card, var(--card-bg))'};
                   color:${isActive ? 'var(--ln-accent-text, var(--accent-text))' : 'var(--ln-text-primary, var(--text-primary))'};
                   font-size:13px;font-weight:${isActive ? 600 : 500};
                   cursor:pointer;white-space:nowrap;">
            ${pin}${esc(item.name)}${removeBtn}
          </button>`;
      })
      .join('');

    host.innerHTML = `
      <div style="display:flex;gap:8px;overflow-x:auto;padding-bottom:6px;
                  scrollbar-width:none;-ms-overflow-style:none;">
        ${chips}
        <button data-act="search-toggle"
          style="display:inline-flex;align-items:center;gap:4px;padding:6px 12px;
                 border-radius:999px;border:1px dashed var(--ln-border, var(--border));background:transparent;
                 color:var(--ln-text-secondary, var(--text-secondary));font-size:13px;cursor:pointer;white-space:nowrap;">
          + ${esc(t('prayer_add_city'))}
        </button>
      </div>
    `;
  }

  private renderHero(): void {
    const host = this.container?.querySelector('[data-region="hero"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.active || !this.activeData) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const locale = this.ctx.i18n.locale;
    const data = this.activeData;
    const np = data.nextPrayer;

    // Normalize hijri shape: server returns { day, month, year, ... }, local
    // gregorianToHijri returns { day, monthName, year }.
    const hijriRaw = data.hijri ?? this.formatHijri(new Date());
    const hijri = {
      day: String(hijriRaw.day ?? ''),
      monthName: 'monthName' in hijriRaw ? hijriRaw.monthName : (hijriRaw.month ?? ''),
      year: String(hijriRaw.year ?? ''),
    };
    const weekdayLabel = new Date().toLocaleDateString(locale, { weekday: 'long' });
    const sourceLabel = this.sourceLabel(data.source);
    const sourceIcon = this.sourceIcon(data.source);
    const dayOfWeek = new Date().getDay();
    const isFriday = dayOfWeek === 5;

    const lang = locale as 'en' | 'fr' | 'ar' | 'es' | 'de';
    const tz = this.active.tz; // undefined for current device location → device tz used
    const initialSeconds = np ? Math.max(0, Math.floor((np.time.getTime() - Date.now()) / 1000)) : 0;
    const npName = np ? prayerTimeService.getPrayerName(np.name as PrayerKey, lang) : '';
    const npTimePair = np ? prayerTimeService.formatTimePair(np.time, lang, tz) : { h24: '', h12: '' };

    // Iqama for the upcoming prayer — only when active is current device
    // location (iqama settings are global per device, not per watched city).
    // Sunrise has no iqama (not a salat); IqamaDelaysConfig keys exclude it.
    let iqamaLine = '';
    if (np && this.active.isCurrent && np.name !== 'sunrise') {
      const iqamaTimes = prayerTimeService.getIqamaTimes();
      const k = np.name as keyof IqamaDelaysConfig;
      const iqamaTime = iqamaTimes ? iqamaTimes[k] : null;
      if (iqamaTime) {
        const iqamaPair = prayerTimeService.formatTimePair(iqamaTime, lang);
        const iqamaSec = Math.max(0, Math.floor((iqamaTime.getTime() - Date.now()) / 1000));
        iqamaLine = `
          <div style="font-size:12px;color:var(--ln-text-secondary, var(--text-secondary));font-variant-numeric:tabular-nums;margin-top:6px;">
            ${esc(t('prayer_iqama_short'))} ${esc(iqamaPair.h24)} · ${esc(iqamaPair.h12)} · ${this.formatCountdown(iqamaSec)}
          </div>`;
      }
    }

    // Travel-aware qasr banner — surfaces only when user opted in AND
    // LocationService flags traveling. Informational; user decides each
    // prayer whether to actually shorten.
    const qasrBanner = prayerTimeService.getQasrApplies()
      ? `
        <div style="margin-top:12px;background:#f59e0b15;border:1px solid #f59e0b40;border-radius:14px;padding:10px 14px;display:flex;align-items:center;gap:10px;">
          <span style="font-size:18px;flex-shrink:0;">✈️</span>
          <div style="flex:1;min-width:0;">
            <div style="font-size:13px;font-weight:700;color:#92400e;">${esc(t('prayer_qasr_active_title') || 'Qasr applies — you are traveling')}</div>
            <div style="font-size:11px;color:#78350f;margin-top:2px;line-height:1.45;">${esc(t('prayer_qasr_active_body') || 'Dhuhr / Asr / Isha may be shortened to 2 rakat. Maghrib stays 3, Fajr stays 2.')}</div>
          </div>
        </div>
      `
      : '';

    host.innerHTML = `
      ${qasrBanner}
      <div style="background:var(--ln-bg-card, var(--card-bg));border-radius:20px;padding:18px;
                  box-shadow:var(--ln-shadow-sm, var(--shadow-sm));margin-top:12px;">
        <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px;flex-wrap:wrap;">
          <div style="min-width:0;flex:1;">
            <div style="display:flex;align-items:baseline;gap:8px;">
              <div style="font-size:13px;font-weight:600;color:var(--ln-text-secondary, var(--text-secondary));text-transform:uppercase;letter-spacing:0.4px;">
                ${esc(t('prayer_next'))} · ${esc(npName)} ${sourceIcon}
              </div>
            </div>
            <!-- Hero number: 24h tabular, big — works as the visual anchor.
                 12h + countdown share the second line so users do "math" once. -->
            <div style="font-size:44px;font-weight:800;line-height:1.05;color:var(--ln-text-primary, var(--text-primary));font-variant-numeric:tabular-nums;margin-top:4px;">
              ${esc(npTimePair.h24)}
            </div>
            <div data-region="hero-countdown" style="font-size:15px;color:var(--ln-accent, var(--accent));font-weight:600;font-variant-numeric:tabular-nums;margin-top:2px;">
              ${esc(npTimePair.h12)} <span style="opacity:0.5;">·</span> ${this.formatCountdown(initialSeconds)}
            </div>
            ${iqamaLine}
          </div>
          <div style="text-align:end;">
            <div style="font-size:13px;font-weight:600;color:var(--ln-text-primary, var(--text-primary));">
              \u{1F4D6} ${esc(hijri.day)} ${esc(hijri.monthName)} ${esc(hijri.year)}
            </div>
            <div style="font-size:11px;color:var(--ln-text-secondary, var(--text-secondary));margin-top:2px;">
              ${esc(weekdayLabel)}${isFriday ? ' · ✨' : ''}
            </div>
            <div style="font-size:11px;color:var(--ln-text-secondary, var(--text-secondary));margin-top:2px;">
              \u{1F4CD} ${esc(this.active.name)}
            </div>
            <div style="font-size:10px;color:var(--ln-text-secondary, var(--text-secondary));margin-top:2px;opacity:0.8;">
              ${esc(sourceLabel)}
            </div>
          </div>
        </div>
        <!-- Phase 3a (2026-05-16) — SourceAttributionStrip: collapsible pill
             surfacing the data sources behind this hero (Hijri authority +
             Prayer calc method + Madhab + Qibla). In-app info popovers, NO
             external links — keeps users in AWENI's Learning Castle per user
             directive 2026-05-16. Mounted after innerHTML below. -->
        <div data-source-attribution style="margin-top:14px;display:block;"></div>
      </div>
    `;

    // Mount the attribution strip into the placeholder. Rows reflect the
    // CURRENT prayer settings (method + madhab + qibla); recreated on every
    // renderHero() so any settings change re-paints the rows.
    this.attrStrip?.unmount();
    const attrSlot = host.querySelector<HTMLElement>('[data-source-attribution]');
    if (attrSlot) {
      const method = prayerTimeService.getMethod();
      const methodLabel = METHOD_DISPLAY.find((m) => m.key === method)?.label ?? method;
      const methodAngles = METHOD_DISPLAY.find((m) => m.key === method)?.angles ?? '';
      const madhab = prayerTimeService.getMadhab();
      // 'Shafi' covers Shafi/Maliki/Hanbali (asr shadow 1×); 'Hanafi' is 2×.
      const madhabLabel = madhab === 'Hanafi'
        ? (t('attribution_madhab_hanafi') || 'Ḥanafī (asr shadow 2×)')
        : (t('attribution_madhab_shafi') || 'Shāfiʿī / Mālikī / Ḥanbalī (asr shadow 1×)');

      this.attrStrip = new SourceAttributionStrip({
        id: 'prayer-hero',
        rows: [
          {
            id: 'prayer-method',
            label: t('attribution_prayer_method_label') || 'Prayer method',
            value: methodAngles ? `${methodLabel} (${methodAngles})` : methodLabel,
            info: t('attribution_prayer_method_info') || 'Calculation method determines Fajr and ʿIshāʾ angles. Auto-mode picks the regional authority based on your country; you can override in Settings.',
          },
          {
            id: 'madhab',
            label: t('attribution_madhab_label') || 'Madhab (asr)',
            value: madhabLabel,
            info: t('attribution_madhab_info') || 'Madhab affects ʿAṣr timing only. Shāfiʿī/Mālikī/Ḥanbalī use shadow ratio 1× (object’s length). Ḥanafī uses 2× (later ʿAṣr).',
          },
          {
            id: 'qibla',
            label: t('attribution_qibla_label') || 'Qibla',
            value: t('attribution_qibla_value') || 'Great-circle bearing to Makkah (GPS + magnetic compass)',
            info: t('attribution_qibla_info') || 'Direction computed from your GPS coordinates to the Kaʿbah using the spherical great-circle formula. Live needle uses your device’s magnetometer; rotate flat for best accuracy.',
          },
        ],
        strings: {
          setCity: t('calendar_hijri_ref_prompt') || 'Set your city for accurate prayer times',
          setCityTooltip: t('calendar_hijri_ref_prompt_tooltip') || 'Prayer times + Hijri date depend on your location. Set your city to use the local authority.',
          expandAria: t('attribution_expand') || 'Show sources used on this page',
          collapseAria: t('attribution_collapse') || 'Hide sources',
          infoAria: t('attribution_info') || 'More about this source',
          hijriAuthorityLabel: t('attribution_hijri_authority') || 'Hijri authority',
        },
      });
      this.attrStrip.mount(attrSlot);
    }
  }

  private renderTodayList(): void {
    const host = this.container?.querySelector('[data-region="today"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.activeData) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const locale = this.ctx.i18n.locale as 'en' | 'fr' | 'ar' | 'es' | 'de';
    const data = this.activeData;
    const now = Date.now();

    // Iqama times (only for current location since iqama settings are global)
    const iqamaTimes = this.active?.isCurrent ? prayerTimeService.getIqamaTimes() : null;
    const iqamaCfg = prayerTimeService.getIqamaDelays();

    const reminders = prayerTimeService.getReminderOffsets();

    const tz = this.active?.tz; // undefined for current device → device tz applies
    // Pad helper for "−HH:MM" style elapsed strings on past prayers, and
    // "in Hh MMm" forward labels.
    const fmtRelative = (deltaSec: number, isPast: boolean): string => {
      const abs = Math.max(0, Math.abs(deltaSec));
      const h = Math.floor(abs / 3600);
      const m = Math.floor((abs % 3600) / 60);
      if (isPast) {
        // Compact "−HH:MM" tabular for elapsed since prayer entered.
        return `−${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      }
      // Compact "Hh MMmin" — no space between digits and unit, prevents
      // mis-reading "2h 09m" as two separate values. Always two-digit
      // minutes for column alignment.
      return h > 0
        ? `${t('glance_strip_in_prefix') || 'in '}${h}h${String(m).padStart(2, '0')}min`
        : `${t('glance_strip_in_prefix') || 'in '}${m}min`;
    };

    const rows = PRAYER_ORDER.map((key) => {
      const time = data[key];
      const isPast = time.getTime() < now;
      const isActive = !isPast && data.nextPrayer?.name === key;
      const name = prayerTimeService.getPrayerName(key, locale);
      const pair = prayerTimeService.formatTimePair(time, locale, tz);
      const deltaSec = Math.floor((time.getTime() - now) / 1000);
      const relativeLabel = fmtRelative(deltaSec, isPast);
      const stateIcon = isActive ? '▶' : isPast ? '✓' : '○';
      const stateColor = isActive ? 'var(--ln-accent, var(--accent))' : isPast ? 'var(--ln-text-secondary, var(--text-secondary))' : 'var(--ln-text-primary, var(--text-primary))';
      const rowOpacity = isPast ? 0.55 : 1;
      const rowBg = isActive ? 'background:rgba(10,10,10,0.05);' : '';
      const reminderMin = reminders[key] ?? 0;
      const reminderBadge = reminderMin > 0
        ? `<span style="font-size:10px;color:var(--ln-text-secondary, var(--text-secondary));margin-inline-start:6px;">\u{1F514} -${reminderMin}m</span>`
        : '';
      // Iqama badge — show iqama time IN PAIR FORMAT (24h + 12h) when set,
      // and the "+Nm" delay tag for context.
      const iqamaBadge = key !== 'sunrise' && iqamaTimes
        ? (() => {
            const iqamaPair = prayerTimeService.formatTimePair(iqamaTimes[key as keyof IqamaDelaysConfig], locale, tz);
            return `<span style="font-size:10px;color:var(--ln-text-secondary, var(--text-secondary));margin-inline-start:6px;font-variant-numeric:tabular-nums;">${esc(t('prayer_iqama_short'))} ${esc(iqamaPair.h24)} · +${iqamaCfg[key as keyof IqamaDelaysConfig]}m</span>`;
          })()
        : '';
      // 5-column tabular grid (per dense-display research): state icon |
      // name | 24h | 12h | countdown. Alignment does the work, no dividers
      // needed beyond the row's hairline border.
      return `
        <li style="display:grid;grid-template-columns:20px 70px 56px 64px 1fr auto;align-items:center;
                   gap:6px;padding:9px 6px;border-top:1px solid var(--ln-border, var(--border));opacity:${rowOpacity};border-radius:8px;${rowBg}">
          <span style="font-size:13px;color:${stateColor};font-weight:700;text-align:center;">${stateIcon}</span>
          <span style="font-size:14px;font-weight:${isActive ? 700 : 600};color:var(--ln-text-primary, var(--text-primary));">${esc(name)}</span>
          <span style="font-size:14px;font-weight:700;color:var(--ln-text-primary, var(--text-primary));font-variant-numeric:tabular-nums;">${esc(pair.h24)}</span>
          <span style="font-size:12px;color:var(--ln-text-secondary, var(--text-secondary));font-variant-numeric:tabular-nums;">${esc(pair.h12)}</span>
          <span style="font-size:12px;font-weight:${isActive ? 700 : 500};color:${isActive ? 'var(--ln-accent, var(--accent))' : 'var(--ln-text-secondary, var(--text-secondary))'};font-variant-numeric:tabular-nums;">${esc(relativeLabel)}</span>
          <span style="display:flex;gap:0;align-items:center;justify-content:flex-end;">${reminderBadge}${iqamaBadge}</span>
        </li>
      `;
    }).join('');

    // Tahajjud window (last third of the night). Only computed for the
    // current device location since the service's SunnahTimes uses its
    // own location state. For non-current cities we could compute inline,
    // but the precision benefit is marginal for the widget badge.
    let tahajjudHtml = '';
    if (this.active?.isCurrent) {
      const sunnah = prayerTimeService.getSunnahTimes();
      if (sunnah) {
        const ltStart = prayerTimeService.formatTime(sunnah.lastThirdOfTheNight, locale, tz);
        const ltEnd = prayerTimeService.formatTime(data.fajr, locale, tz);
        tahajjudHtml = `
          <li style="display:flex;align-items:center;gap:8px;padding:10px 4px;
                     border-top:1px solid var(--ln-border, var(--border));font-size:13px;
                     color:var(--ln-text-secondary, var(--text-secondary));">
            \u{1F319} <span>${esc(t('prayer_tahajjud_window'))} <strong style="color:var(--ln-text-primary, var(--text-primary));font-variant-numeric:tabular-nums;">${esc(ltStart)} → ${esc(ltEnd)}</strong></span>
          </li>
        `;
      }
    }

    host.innerHTML = `
      <div style="background:var(--ln-bg-card, var(--card-bg));border-radius:20px;padding:14px;
                  box-shadow:var(--ln-shadow-sm, var(--shadow-sm));margin-top:12px;">
        <h3 style="font-size:14px;font-weight:700;color:var(--ln-text-primary, var(--text-primary));margin:0 0 4px;">
          ${esc(t('prayer_today'))}
        </h3>
        <ul style="list-style:none;padding:0;margin:0;">${rows}${tahajjudHtml}</ul>
      </div>
    `;
  }

  private renderSunArc(): void {
    const host = this.container?.querySelector('[data-region="sun-arc"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.activeData) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const locale = this.ctx.i18n.locale as 'en' | 'fr' | 'ar' | 'es' | 'de';
    const data = this.activeData;
    const now = Date.now();

    // Position "now" along the arc, normalized 0..1 between Fajr and Isha.
    const start = data.fajr.getTime();
    const end = data.isha.getTime();
    const range = Math.max(1, end - start);
    const progress = Math.max(0, Math.min(1, (now - start) / range));

    // SVG arc: half-circle, 0..π. Width 320, height 120.
    const W = 320, H = 120, cx = W / 2, cy = H, r = 96;
    const angleAtProgress = Math.PI * (1 - progress); // π → 0 from left to right
    const sx = cx + r * Math.cos(angleAtProgress);
    const sy = cy - r * Math.sin(angleAtProgress);

    const tz = this.active?.tz;
    // Place each prayer marker on the arc by its time fraction.
    const marks = PRAYER_ORDER.map((key) => {
      const time = data[key].getTime();
      const f = Math.max(0, Math.min(1, (time - start) / range));
      const a = Math.PI * (1 - f);
      const x = cx + r * Math.cos(a);
      const y = cy - r * Math.sin(a);
      const isPast = time < now;
      return { key, x, y, isPast, label: prayerTimeService.formatTime(data[key], locale, tz) };
    });

    const arcPath = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;

    const markCircles = marks.map((m) => `
      <circle cx="${m.x.toFixed(1)}" cy="${m.y.toFixed(1)}" r="3"
        fill="${m.isPast ? 'var(--ln-text-secondary, var(--text-secondary))' : 'var(--ln-accent, var(--accent))'}"/>
    `).join('');

    const markLabels = marks.map((m) => {
      const align = m.x < cx - 30 ? 'start' : m.x > cx + 30 ? 'end' : 'middle';
      return `
        <text x="${m.x.toFixed(1)}" y="${(m.y - 8).toFixed(1)}"
          text-anchor="${align}" font-size="9" fill="var(--ln-text-secondary, var(--text-secondary))">
          ${esc(prayerTimeService.getPrayerName(m.key, locale).slice(0, 6))}
        </text>
      `;
    }).join('');

    host.innerHTML = `
      <div style="background:var(--ln-bg-card, var(--card-bg));border-radius:20px;padding:14px;
                  box-shadow:var(--ln-shadow-sm, var(--shadow-sm));margin-top:12px;">
        <h3 style="font-size:14px;font-weight:700;color:var(--ln-text-primary, var(--text-primary));margin:0 0 8px;">
          ${esc(t('prayer_sun_arc'))}
        </h3>
        <svg viewBox="0 0 ${W} ${H}" width="100%" height="120" role="img" aria-label="${escAttr(t('prayer_sun_arc'))}">
          <path d="${arcPath}" fill="none" stroke="var(--ln-border, var(--border))" stroke-width="2"/>
          ${markCircles}
          ${markLabels}
          <circle cx="${sx.toFixed(1)}" cy="${sy.toFixed(1)}" r="6"
            fill="var(--ln-accent-ceremonial, #9C8348)" stroke="var(--ln-bg-card, var(--card-bg))" stroke-width="2"/>
        </svg>
      </div>
    `;
  }

  private renderQibla(): void {
    const host = this.container?.querySelector('[data-region="qibla"]') as HTMLElement | null;
    if (!host || !this.ctx || !this.activeData) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const qibla = this.activeData.qiblaDirection;

    // The visual needle rotates relative to device orientation when compass is enabled.
    const needleAngle = this.compassEnabled ? qibla - this.compassHeading : qibla;

    // Show the iOS gesture button only on iOS Safari (requestPermission API).
    const canRequest = typeof DeviceOrientationEvent !== 'undefined'
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      && typeof (DeviceOrientationEvent as any).requestPermission === 'function';
    const enableBtn = !this.compassEnabled
      ? `<button data-act="qibla-enable"
          style="margin-top:8px;padding:8px 14px;border-radius:10px;border:none;
                 background:var(--ln-accent, var(--accent));color:var(--ln-accent-text, var(--accent-text));
                 font-size:13px;font-weight:600;cursor:pointer;">
          ${esc(t(canRequest ? 'prayer_qibla_enable_ios' : 'prayer_qibla_enable'))}
        </button>`
      : `<button data-act="qibla-disable"
          style="margin-top:8px;padding:8px 14px;border-radius:10px;
                 border:1px solid var(--ln-border, var(--border));background:transparent;
                 color:var(--ln-text-secondary, var(--text-secondary));font-size:13px;cursor:pointer;">
          ${esc(t('prayer_qibla_disable'))}
        </button>`;

    host.innerHTML = `
      <div style="background:var(--ln-bg-card, var(--card-bg));border-radius:20px;padding:14px;
                  box-shadow:var(--ln-shadow-sm, var(--shadow-sm));margin-top:12px;text-align:center;">
        <h3 style="font-size:14px;font-weight:700;color:var(--ln-text-primary, var(--text-primary));margin:0 0 8px;text-align:start;">
          ${esc(t('prayer_qibla'))}
        </h3>
        <svg viewBox="0 0 200 200" width="200" height="200" role="img" aria-label="${escAttr(t('prayer_qibla'))}">
          <circle cx="100" cy="100" r="92" fill="none" stroke="var(--ln-border, var(--border))" stroke-width="2"/>
          <text x="100" y="16" text-anchor="middle" font-size="11" fill="var(--ln-text-secondary, var(--text-secondary))">N</text>
          <text x="184" y="104" text-anchor="middle" font-size="11" fill="var(--ln-text-secondary, var(--text-secondary))">E</text>
          <text x="100" y="194" text-anchor="middle" font-size="11" fill="var(--ln-text-secondary, var(--text-secondary))">S</text>
          <text x="16" y="104" text-anchor="middle" font-size="11" fill="var(--ln-text-secondary, var(--text-secondary))">W</text>
          <g transform="rotate(${needleAngle.toFixed(1)} 100 100)">
            <polygon points="100,18 108,108 100,100 92,108"
              fill="var(--ln-accent-ceremonial, #9C8348)" stroke="var(--ln-bg-card, var(--card-bg))" stroke-width="1"/>
            <circle cx="100" cy="100" r="5" fill="var(--ln-text-primary, var(--text-primary))"/>
          </g>
        </svg>
        <div style="font-size:14px;font-weight:700;color:var(--ln-text-primary, var(--text-primary));margin-top:4px;font-variant-numeric:tabular-nums;">
          ${qibla.toFixed(1)}°
        </div>
        <div style="font-size:11px;color:var(--ln-text-secondary, var(--text-secondary));">
          ${esc(t('prayer_qibla_from_north'))}
        </div>
        ${enableBtn}
      </div>
    `;
  }

  private renderCalendar(): void {
    const host = this.container?.querySelector('[data-region="calendar"]') as HTMLElement | null;
    if (!host || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    const locale = this.ctx.i18n.locale;

    if (!this.monthDays || this.monthDays.length === 0) {
      // Don't render the card while we have no data — keeps visual noise low.
      host.innerHTML = '';
      return;
    }

    const now = new Date();
    const todayIso = now.toISOString().split('T')[0];
    const monthLabel = now.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

    const rows = this.monthDays.map((day) => {
      const iso = (day.iso ?? day.date) as string;
      const timings = (day.timings ?? {}) as Record<string, string>;
      const hijri = day.hijri as { day?: string; month?: string } | undefined;
      const isToday = iso === todayIso;
      const dayNum = iso ? Number(iso.split('-')[2]) : '?';
      return `
        <li style="display:grid;grid-template-columns:30px 1fr;gap:8px;padding:6px 4px;
                   border-top:1px solid var(--ln-border, var(--border));
                   ${isToday ? 'background:rgba(156,131,72,0.08);border-radius:8px;padding-inline:8px;' : ''}">
          <span style="font-size:13px;font-weight:${isToday ? 700 : 600};color:${isToday ? 'var(--ln-accent-ceremonial, #9C8348)' : 'var(--ln-text-primary, var(--text-primary))'};">
            ${dayNum}${hijri ? `<br><span style='font-size:9px;color:var(--ln-text-secondary, var(--text-secondary));font-weight:400;'>${esc(String(hijri.day ?? ''))}</span>` : ''}
          </span>
          <span style="font-size:11px;color:var(--ln-text-secondary, var(--text-secondary));font-variant-numeric:tabular-nums;">
            F ${esc(timings.Fajr ?? '')} · D ${esc(timings.Dhuhr ?? '')} · A ${esc(timings.Asr ?? '')} · M ${esc(timings.Maghrib ?? '')} · I ${esc(timings.Isha ?? '')}
          </span>
        </li>
      `;
    }).join('');

    host.innerHTML = `
      <div style="background:var(--ln-bg-card, var(--card-bg));border-radius:20px;padding:14px;
                  box-shadow:var(--ln-shadow-sm, var(--shadow-sm));margin-top:12px;">
        <h3 style="font-size:14px;font-weight:700;color:var(--ln-text-primary, var(--text-primary));margin:0 0 4px;">
          ${esc(t('prayer_month_calendar'))} · <span style="color:var(--ln-text-secondary, var(--text-secondary));font-weight:500;">${esc(monthLabel)}</span>
        </h3>
        <ul style="list-style:none;padding:0;margin:0;max-height:360px;overflow-y:auto;">${rows}</ul>
      </div>
    `;
  }

  private renderSettings(): void {
    const host = this.container?.querySelector('[data-region="settings"]') as HTMLElement | null;
    if (!host || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    const method = prayerTimeService.getMethod();
    const madhab = prayerTimeService.getMadhab();
    const tune = prayerTimeService.getTune();
    const iqama = prayerTimeService.getIqamaDelays();
    const reminders = prayerTimeService.getReminderOffsets();
    const highLat = prayerTimeService.getHighLatRule();
    const midnight = prayerTimeService.getMidnightMode();
    const shafaq = prayerTimeService.getShafaq();
    const autoMode = prayerTimeService.isAutoMethod();
    const lastCC = prayerTimeService.getLastCountryCode();
    // Build the "Why this method" reasoning so the user understands the
    // current calc method is regional and authoritative — not a default.
    const methodLabel = METHOD_DISPLAY.find((m) => m.key === method)?.label ?? method;
    const autoHint = autoMode && lastCC
      ? `${esc(t('prayer_auto_hint_using') || 'Using')} <strong>${esc(methodLabel)}</strong> ${esc(t('prayer_auto_hint_because') || '— matched to your country')} <span style="font-variant:all-small-caps;letter-spacing:0.5px;">(${esc(lastCC)})</span>.`
      : autoMode
        ? esc(t('prayer_auto_hint_no_country') || 'Auto mode is on — set your city to apply the right regional method.')
        : esc(t('prayer_auto_hint_off') || 'Manual mode — calculation method is locked to your choice.');

    const methodOptions = METHOD_DISPLAY
      .map((m) => `<option value="${m.key}" ${m.key === method ? 'selected' : ''}>${esc(m.label)} (${esc(m.angles)})</option>`)
      .join('');

    const tuneRows = PRAYER_ORDER.map((p) => `
      <div style="display:grid;grid-template-columns:90px 1fr 50px;gap:8px;align-items:center;padding:4px 0;">
        <label style="font-size:13px;color:var(--ln-text-primary, var(--text-primary));">${esc(prayerTimeService.getPrayerName(p))}</label>
        <input type="range" data-act="set-tune" data-prayer="${p}" min="-30" max="30" step="1"
          value="${tune[p]}" style="width:100%;"/>
        <span data-tune-val="${p}" style="font-size:12px;text-align:end;font-variant-numeric:tabular-nums;color:var(--ln-text-secondary, var(--text-secondary));">
          ${tune[p] >= 0 ? '+' : ''}${tune[p]} m
        </span>
      </div>
    `).join('');

    const iqamaRows = IQAMA_KEYS.map((p) => {
      const prayerName = prayerTimeService.getPrayerName(p);
      const ariaLabel = `${prayerName} ${t('prayer_iqama_offset_min') || 'iqama offset (minutes after athan)'}`;
      return `
      <div style="display:grid;grid-template-columns:90px 1fr 50px;gap:8px;align-items:center;padding:4px 0;">
        <label style="font-size:13px;color:var(--ln-text-primary, var(--text-primary));" id="iqama-label-${p}">${esc(prayerName)}</label>
        <input type="range" data-act="set-iqama" data-prayer="${p}" min="0" max="60" step="1"
          value="${iqama[p]}" style="width:100%;"
          aria-labelledby="iqama-label-${p}"
          aria-label="${escAttr(ariaLabel)}"
          aria-valuemin="0" aria-valuemax="60" aria-valuenow="${iqama[p]}" aria-valuetext="${iqama[p]} ${t('prayer_iqama_offset_min') || 'minutes'}"/>
        <span data-iqama-val="${p}" style="font-size:12px;text-align:end;font-variant-numeric:tabular-nums;color:var(--ln-text-secondary, var(--text-secondary));">
          +${iqama[p]} m
        </span>
      </div>
    `;
    }).join('');

    const reminderRows = PRAYER_ORDER.filter((p) => p !== 'sunrise').map((p) => `
      <div style="display:grid;grid-template-columns:90px 1fr 50px;gap:8px;align-items:center;padding:4px 0;">
        <label style="font-size:13px;color:var(--ln-text-primary, var(--text-primary));">${esc(prayerTimeService.getPrayerName(p))}</label>
        <input type="range" data-act="set-reminder" data-prayer="${p}" min="0" max="60" step="5"
          value="${reminders[p]}" style="width:100%;"/>
        <span data-reminder-val="${p}" style="font-size:12px;text-align:end;font-variant-numeric:tabular-nums;color:var(--ln-text-secondary, var(--text-secondary));">
          ${reminders[p]} m
        </span>
      </div>
    `).join('');

    const shafaqVisible = method === 'MoonsightingCommittee';

    const settingsBody = !this.settingsOpen ? '' : `
      <div style="border-top:1px solid var(--ln-border, var(--border));padding-top:12px;margin-top:12px;display:grid;gap:14px;">

        <!-- Auto-mode toggle + "why this method" hint -->
        <div style="background:rgba(10,10,10,0.04);border-radius:12px;padding:12px;display:grid;gap:8px;">
          <label style="display:flex;align-items:center;justify-content:space-between;gap:10px;cursor:pointer;">
            <span style="display:flex;align-items:center;gap:8px;font-size:13px;font-weight:600;color:var(--ln-text-primary, var(--text-primary));">
              ⚡ ${esc(t('prayer_auto_label') || 'Auto: pick method by country')}
            </span>
            <span style="position:relative;display:inline-block;width:42px;height:24px;flex-shrink:0;">
              <input type="checkbox" data-act="set-auto-method" ${autoMode ? 'checked' : ''}
                     style="opacity:0;width:0;height:0;position:absolute;"/>
              <span aria-hidden="true" style="position:absolute;inset:0;background:${autoMode ? 'var(--ln-accent, var(--accent))' : 'var(--ln-border, var(--border))'};border-radius:999px;transition:background var(--motion-duration-base);"></span>
              <span aria-hidden="true" style="position:absolute;top:2px;${autoMode ? 'left:20px' : 'left:2px'};width:20px;height:20px;background:#fff;border-radius:50%;transition:left var(--motion-duration-base);box-shadow:0 1px 3px rgba(0,0,0,0.3);"></span>
            </span>
          </label>
          <div style="font-size:11px;color:var(--ln-text-secondary, var(--text-secondary));line-height:1.4;">${autoHint}</div>
        </div>

        <!-- Calculation method -->
        <label style="display:grid;gap:4px;">
          <span style="font-size:12px;font-weight:600;color:var(--ln-text-secondary, var(--text-secondary));">${esc(t('prayer_method_label'))}${autoMode ? ` <span style="font-weight:500;font-size:10px;opacity:0.7;">(${esc(t('prayer_auto_managed') || 'auto')})</span>` : ''}</span>
          <select data-act="set-method" ${autoMode ? 'disabled' : ''} style="padding:8px 10px;border-radius:10px;border:1px solid var(--ln-border, var(--border));
                  background:var(--ln-bg-primary, var(--bg-primary));color:var(--ln-text-primary, var(--text-primary));font-size:13px;${autoMode ? 'opacity:0.55;cursor:not-allowed;' : ''}">
            ${methodOptions}
          </select>
        </label>

        <!-- Madhab -->
        <fieldset style="border:none;padding:0;margin:0;${autoMode ? 'opacity:0.55;' : ''}">
          <legend style="font-size:12px;font-weight:600;color:var(--ln-text-secondary, var(--text-secondary));margin-bottom:4px;">
            ${esc(t('prayer_madhab_label'))}${autoMode ? ` <span style="font-weight:500;font-size:10px;opacity:0.7;">(${esc(t('prayer_auto_managed') || 'auto')})</span>` : ''}
          </legend>
          <label style="display:inline-flex;align-items:center;gap:6px;font-size:13px;margin-inline-end:14px;cursor:${autoMode ? 'not-allowed' : 'pointer'};">
            <input type="radio" name="madhab" value="Shafi" data-act="set-madhab" ${madhab === 'Shafi' ? 'checked' : ''} ${autoMode ? 'disabled' : ''}/>
            ${esc(t('prayer_madhab_shafi'))}
          </label>
          <label style="display:inline-flex;align-items:center;gap:6px;font-size:13px;cursor:${autoMode ? 'not-allowed' : 'pointer'};">
            <input type="radio" name="madhab" value="Hanafi" data-act="set-madhab" ${madhab === 'Hanafi' ? 'checked' : ''} ${autoMode ? 'disabled' : ''}/>
            ${esc(t('prayer_madhab_hanafi'))}
          </label>
        </fieldset>

        <!-- High-latitude rule -->
        <label style="display:grid;gap:4px;">
          <span style="font-size:12px;font-weight:600;color:var(--ln-text-secondary, var(--text-secondary));">${esc(t('prayer_highlat_label'))}</span>
          <select data-act="set-highlat" style="padding:8px 10px;border-radius:10px;border:1px solid var(--ln-border, var(--border));
                  background:var(--ln-bg-primary, var(--bg-primary));color:var(--ln-text-primary, var(--text-primary));font-size:13px;">
            <option value="auto" ${highLat === 'auto' ? 'selected' : ''}>${esc(t('prayer_highlat_auto'))}</option>
            <option value="middleOfNight" ${highLat === 'middleOfNight' ? 'selected' : ''}>${esc(t('prayer_highlat_middle'))}</option>
            <option value="seventhOfNight" ${highLat === 'seventhOfNight' ? 'selected' : ''}>${esc(t('prayer_highlat_seventh'))}</option>
            <option value="twilightAngle" ${highLat === 'twilightAngle' ? 'selected' : ''}>${esc(t('prayer_highlat_angle'))}</option>
          </select>
        </label>

        <!-- Midnight mode -->
        <label style="display:grid;gap:4px;">
          <span style="font-size:12px;font-weight:600;color:var(--ln-text-secondary, var(--text-secondary));">${esc(t('prayer_midnight_label'))}</span>
          <select data-act="set-midnight" style="padding:8px 10px;border-radius:10px;border:1px solid var(--ln-border, var(--border));
                  background:var(--ln-bg-primary, var(--bg-primary));color:var(--ln-text-primary, var(--text-primary));font-size:13px;">
            <option value="standard" ${midnight === 'standard' ? 'selected' : ''}>${esc(t('prayer_midnight_standard'))}</option>
            <option value="jafari" ${midnight === 'jafari' ? 'selected' : ''}>${esc(t('prayer_midnight_jafari'))}</option>
          </select>
        </label>

        ${!shafaqVisible ? '' : `
        <!-- Shafaq (only for Moonsighting Committee) -->
        <label style="display:grid;gap:4px;">
          <span style="font-size:12px;font-weight:600;color:var(--ln-text-secondary, var(--text-secondary));">${esc(t('prayer_shafaq_label'))}</span>
          <select data-act="set-shafaq" style="padding:8px 10px;border-radius:10px;border:1px solid var(--ln-border, var(--border));
                  background:var(--ln-bg-primary, var(--bg-primary));color:var(--ln-text-primary, var(--text-primary));font-size:13px;">
            <option value="general" ${shafaq === 'general' ? 'selected' : ''}>${esc(t('prayer_shafaq_general'))}</option>
            <option value="ahmer" ${shafaq === 'ahmer' ? 'selected' : ''}>${esc(t('prayer_shafaq_ahmer'))}</option>
            <option value="abyad" ${shafaq === 'abyad' ? 'selected' : ''}>${esc(t('prayer_shafaq_abyad'))}</option>
          </select>
        </label>`}

        <!-- Per-prayer tune -->
        <div>
          <div style="font-size:12px;font-weight:600;color:var(--ln-text-secondary, var(--text-secondary));margin-bottom:4px;">
            ${esc(t('prayer_tune_label'))}
          </div>
          ${tuneRows}
        </div>

        <!-- Reminder offsets -->
        <div>
          <div style="font-size:12px;font-weight:600;color:var(--ln-text-secondary, var(--text-secondary));margin-bottom:4px;">
            ${esc(t('prayer_reminders_label'))}
          </div>
          ${reminderRows}
        </div>

        <!-- Iqama delays — Tier 2 #16. Real mosques announce iqama 5–25 min
             after athan; persisted to localStorage aweni_prayer_iqama and
             best-effort mirrored to /v1/preferences. Each slider is fully
             keyboard- and screen-reader-accessible (aria-labelledby + valuetext). -->
        <div>
          <div style="font-size:12px;font-weight:700;color:var(--ln-text-secondary, var(--text-secondary));margin-bottom:2px;text-transform:uppercase;letter-spacing:0.4px;">
            ${esc(t('prayer_iqama_offset_section') || 'Iqama offsets')}
          </div>
          <div style="font-size:11px;color:var(--ln-text-secondary, var(--text-secondary));margin-bottom:8px;">
            ${esc(t('prayer_iqama_label'))}
          </div>
          ${iqamaRows}
          <label style="display:flex;align-items:center;gap:10px;margin-top:10px;padding:8px 10px;border-radius:10px;background:rgba(10,10,10,0.04);cursor:pointer;">
            <input type="checkbox" data-act="set-iqama-remind" ${prayerTimeService.getIqamaReminderEnabled() ? 'checked' : ''}
              aria-label="${escAttr(t('prayer_iqama_remind_toggle') || 'Notify me at iqama time')}"
              style="width:18px;height:18px;cursor:pointer;"/>
            <span style="font-size:12px;color:var(--ln-text-primary, var(--text-primary));">
              🔔 ${esc(t('prayer_iqama_remind_toggle') || 'Notify me at iqama time')}
            </span>
          </label>
          <label style="display:flex;align-items:flex-start;gap:10px;margin-top:8px;padding:8px 10px;border-radius:10px;background:rgba(10,10,10,0.04);cursor:pointer;">
            <input type="checkbox" data-act="set-force-local" ${prayerTimeService.getForceLocalCalc() ? 'checked' : ''}
              aria-label="${escAttr(t('prayer_force_local_toggle') || 'Match my local mosque (offline calc)')}"
              style="width:18px;height:18px;cursor:pointer;margin-top:2px;"/>
            <span style="display:flex;flex-direction:column;gap:2px;font-size:12px;color:var(--ln-text-primary, var(--text-primary));">
              <span>📍 ${esc(t('prayer_force_local_toggle') || 'Match my local mosque (offline calc)')}</span>
              <span style="font-size:10px;color:var(--ln-text-secondary, var(--text-secondary));">${esc(t('prayer_force_local_hint') || 'Use the offline Adhan.js calc when your local mosque schedule differs from the AWENI default.')}</span>
            </span>
          </label>
          <!-- Travel-aware qasr opt-in. Only meaningful when LocationService
               flags the user as traveling; AWENI surfaces a hint then. -->
          <label style="display:flex;align-items:flex-start;gap:10px;margin-top:8px;padding:8px 10px;border-radius:10px;background:rgba(10,10,10,0.04);cursor:pointer;">
            <input type="checkbox" data-act="set-qasr" ${prayerTimeService.getQasrEnabled() ? 'checked' : ''}
              aria-label="${escAttr(t('prayer_qasr_toggle') || 'Travel-aware qasr')}"
              style="width:18px;height:18px;cursor:pointer;margin-top:2px;"/>
            <span style="display:flex;flex-direction:column;gap:2px;font-size:12px;color:var(--ln-text-primary, var(--text-primary));">
              <span>✈️ ${esc(t('prayer_qasr_toggle') || 'Travel-aware qasr')}</span>
              <span style="font-size:10px;color:var(--ln-text-secondary, var(--text-secondary));">${esc(t('prayer_qasr_hint') || 'When traveling, AWENI shows a reminder to shorten Dhuhr / Asr / Isha to 2 rakat. You decide each prayer.')}</span>
            </span>
          </label>
          <!-- Manual Hijri ±1 day adjust. Useful when local moonsighting
               announces a different day than the tabular Umm al-Qura. -->
          <div style="margin-top:8px;padding:8px 10px;border-radius:10px;background:rgba(10,10,10,0.04);">
            <div style="font-size:12px;font-weight:600;color:var(--ln-text-primary, var(--text-primary));margin-bottom:4px;">
              📅 ${esc(t('prayer_hijri_offset_title') || 'Hijri date adjust')}
            </div>
            <div style="font-size:10px;color:var(--ln-text-secondary, var(--text-secondary));margin-bottom:6px;">
              ${esc(t('prayer_hijri_offset_hint') || 'Shift the displayed Hijri date by ±1 day to match your local moonsighting.')}
            </div>
            <div role="radiogroup" aria-label="${escAttr(t('prayer_hijri_offset_title') || 'Hijri date adjust')}" style="display:flex;gap:6px;">
              ${[-2, -1, 0, 1, 2].map((v) => {
                const active = prayerTimeService.getHijriOffset() === v;
                const label = v === 0 ? (t('prayer_hijri_offset_today') || 'Today') : (v > 0 ? `+${v}` : String(v).replace('-', '−'));
                return `
                  <button type="button" data-act="set-hijri-offset" data-value="${v}"
                    role="radio" aria-checked="${active ? 'true' : 'false'}"
                    style="flex:1;min-height:36px;padding:6px 10px;border-radius:8px;border:1px solid ${active ? 'var(--ln-accent, #0A0A0A)' : 'var(--ln-border, var(--border, rgba(0,0,0,0.10)))'};background:${active ? 'var(--ln-accent, #0A0A0A)' : 'transparent'};color:${active ? 'var(--ln-accent-text, #fff)' : 'inherit'};font-size:13px;font-weight:600;cursor:pointer;font-variant-numeric:tabular-nums;">
                    ${label}
                  </button>
                `;
              }).join('')}
            </div>
          </div>
        </div>
      </div>
    `;

    host.innerHTML = `
      <div style="background:var(--ln-bg-card, var(--card-bg));border-radius:20px;padding:14px;
                  box-shadow:var(--ln-shadow-sm, var(--shadow-sm));margin-top:12px;">
        <button data-act="toggle-settings" aria-expanded="${this.settingsOpen}"
          style="display:flex;width:100%;align-items:center;justify-content:space-between;background:transparent;
                 border:none;padding:0;cursor:pointer;color:inherit;text-align:start;">
          <h3 style="font-size:14px;font-weight:700;color:var(--ln-text-primary, var(--text-primary));margin:0;">
            ⚙️ ${esc(t('prayer_settings'))}
          </h3>
          <span style="font-size:14px;color:var(--ln-text-secondary, var(--text-secondary));">${this.settingsOpen ? '▲' : '▼'}</span>
        </button>
        ${settingsBody}
      </div>
    `;
  }

  private renderSearch(): void {
    const host = this.container?.querySelector('[data-region="search"]') as HTMLElement | null;
    if (!host || !this.ctx) return;
    if (!this.searchOpen) { host.innerHTML = ''; return; }
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    // Render the panel + input ONCE per searchOpen toggle. Subsequent
    // calls only update the results sub-region so typing never destroys
    // the input element (focus + caret position preserved).
    const existingInput = host.querySelector('[data-act="search-input"]');
    if (!existingInput) {
      host.innerHTML = `
        <div style="background:var(--ln-bg-card, var(--card-bg));border-radius:20px;padding:14px;box-shadow:var(--ln-shadow-sm, var(--shadow-sm));">
          <label style="display:block;font-size:12px;color:var(--ln-text-secondary, var(--text-secondary));margin-bottom:6px;">
            ${esc(t('prayer_search_label'))}
          </label>
          <input type="text" data-act="search-input" autocomplete="off"
            placeholder="${escAttr(t('prayer_search_placeholder'))}"
            style="width:100%;padding:10px 12px;border-radius:12px;border:1px solid var(--ln-border, var(--border));
                   background:var(--ln-bg-primary, var(--bg-primary));color:var(--ln-text-primary, var(--text-primary));font-size:14px;
                   box-sizing:border-box;"/>
          <div data-region="search-results" style="margin-top:8px;min-height:24px;"></div>
        </div>
      `;
      const input = host.querySelector('[data-act="search-input"]') as HTMLInputElement | null;
      if (input) {
        input.focus();
        input.addEventListener('input', () => {
          const q = input.value;
          if (this.searchTimer) clearTimeout(this.searchTimer);
          if (q.trim().length < 2) {
            // Empty input — surface the bundled "popular cities" list so
            // the user has something to pick from immediately (per user
            // feedback: "show favorites + big cities BEFORE typing").
            this.searchResults = this.getBundledPopularResults();
            this.searching = false;
            this.renderSearchResults();
            return;
          }
          this.searching = true;
          this.renderSearchResults();
          const ref = input;
          this.searchTimer = window.setTimeout(async () => {
            const lang = this.ctx?.i18n.locale.slice(0, 2) || 'en';
            const results = await this.weatherService.geocodeCity(ref.value, lang);
            if (ref.value === q) {
              this.searchResults = results;
              this.searching = false;
              this.renderSearchResults();
            }
          }, 250);
        });
      }
    }

    // Initial population: show bundled popular cities right when the user
    // opens the search panel, before any typing happens. This eliminates
    // the dead "blank results" state and discourages typos that hit weird
    // OM matches like "White House" (the bug reported 2026-05-06).
    if (this.searchResults.length === 0) {
      this.searchResults = this.getBundledPopularResults();
    }
    this.renderSearchResults();
  }

  /** ~30 of the most-asked cities for AWENI's audience (Muslim-majority +
   *  diaspora hubs) packed as GeocodeResult-shaped objects so the existing
   *  result row can render them without a special path. Coordinates from
   *  OpenStreetMap (CC-BY-SA), timezones from IANA. */
  private getBundledPopularResults(): GeocodeResult[] {
    return [
      { id: 1, name: 'Casablanca', country: 'Morocco', country_code: 'MA', latitude: 33.5731, longitude: -7.5898, timezone: 'Africa/Casablanca' },
      { id: 2, name: 'Mecca', country: 'Saudi Arabia', country_code: 'SA', latitude: 21.4225, longitude: 39.8262, timezone: 'Asia/Riyadh' },
      { id: 3, name: 'Medina', country: 'Saudi Arabia', country_code: 'SA', latitude: 24.5247, longitude: 39.5692, timezone: 'Asia/Riyadh' },
      { id: 4, name: 'Cairo', country: 'Egypt', country_code: 'EG', latitude: 30.0444, longitude: 31.2357, timezone: 'Africa/Cairo' },
      { id: 5, name: 'Istanbul', country: 'Türkiye', country_code: 'TR', latitude: 41.0082, longitude: 28.9784, timezone: 'Europe/Istanbul' },
      { id: 6, name: 'Dubai', country: 'United Arab Emirates', country_code: 'AE', latitude: 25.2048, longitude: 55.2708, timezone: 'Asia/Dubai' },
      { id: 7, name: 'Riyadh', country: 'Saudi Arabia', country_code: 'SA', latitude: 24.7136, longitude: 46.6753, timezone: 'Asia/Riyadh' },
      { id: 8, name: 'Jakarta', country: 'Indonesia', country_code: 'ID', latitude: -6.2088, longitude: 106.8456, timezone: 'Asia/Jakarta' },
      { id: 9, name: 'Karachi', country: 'Pakistan', country_code: 'PK', latitude: 24.8607, longitude: 67.0011, timezone: 'Asia/Karachi' },
      { id: 10, name: 'Kuala Lumpur', country: 'Malaysia', country_code: 'MY', latitude: 3.1390, longitude: 101.6869, timezone: 'Asia/Kuala_Lumpur' },
      { id: 11, name: 'Paris', country: 'France', country_code: 'FR', latitude: 48.8566, longitude: 2.3522, timezone: 'Europe/Paris' },
      { id: 12, name: 'London', country: 'United Kingdom', country_code: 'GB', latitude: 51.5074, longitude: -0.1278, timezone: 'Europe/London' },
      { id: 13, name: 'Berlin', country: 'Germany', country_code: 'DE', latitude: 52.5200, longitude: 13.4050, timezone: 'Europe/Berlin' },
      { id: 14, name: 'New York', country: 'United States', country_code: 'US', latitude: 40.7128, longitude: -74.0060, timezone: 'America/New_York' },
      { id: 15, name: 'Algiers', country: 'Algeria', country_code: 'DZ', latitude: 36.7538, longitude: 3.0588, timezone: 'Africa/Algiers' },
      { id: 16, name: 'Tunis', country: 'Tunisia', country_code: 'TN', latitude: 36.8065, longitude: 10.1815, timezone: 'Africa/Tunis' },
      { id: 17, name: 'Rabat', country: 'Morocco', country_code: 'MA', latitude: 34.0209, longitude: -6.8416, timezone: 'Africa/Casablanca' },
      { id: 18, name: 'Marrakech', country: 'Morocco', country_code: 'MA', latitude: 31.6295, longitude: -7.9811, timezone: 'Africa/Casablanca' },
      { id: 19, name: 'Sydney', country: 'Australia', country_code: 'AU', latitude: -33.8688, longitude: 151.2093, timezone: 'Australia/Sydney' },
      { id: 20, name: 'Toronto', country: 'Canada', country_code: 'CA', latitude: 43.6532, longitude: -79.3832, timezone: 'America/Toronto' },
    ] as GeocodeResult[];
  }

  /** Updates only the results region — leaves the input intact so typing
   *  never loses focus / caret position (was the bug 2026-05-06). */
  private renderSearchResults(): void {
    const host = this.container?.querySelector('[data-region="search"]') as HTMLElement | null;
    const results = host?.querySelector('[data-region="search-results"]') as HTMLElement | null;
    if (!results || !this.ctx) return;
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);

    const items = this.searchResults
      .map((r) => {
        // Country code as a visible chip — disambiguates duplicates like
        // "Casablanca, MA" vs anything else with the same name. Reported
        // 2026-05-06 (user picked the wrong city in the OM list).
        const cc = r.country_code ? r.country_code.toUpperCase() : '';
        const detail = [r.admin1, r.country].filter(Boolean).join(', ');
        return `
          <button data-act="add-result" data-lat="${r.latitude}" data-lng="${r.longitude}"
            data-name="${escAttr(r.name)}" data-cc="${escAttr(r.country_code || '')}"
            data-admin1="${escAttr(r.admin1 || '')}" data-tz="${escAttr(r.timezone || '')}"
            style="display:flex;width:100%;align-items:center;justify-content:space-between;gap:8px;
                   text-align:start;background:var(--ln-bg-secondary, var(--bg-secondary));
                   border:none;border-radius:12px;padding:10px 12px;margin-top:6px;cursor:pointer;">
            <div style="min-width:0;flex:1;">
              <div style="font-size:14px;font-weight:600;color:var(--ln-text-primary, var(--text-primary));">
                ${esc(r.name)}
              </div>
              <div style="font-size:12px;color:var(--ln-text-secondary, var(--text-secondary));margin-top:2px;
                          white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">
                ${esc(detail)}
              </div>
            </div>
            ${cc ? `<span style="flex-shrink:0;font-size:11px;font-weight:700;letter-spacing:0.5px;
                                  padding:3px 7px;border-radius:6px;
                                  background:rgba(10,10,10,0.06);
                                  color:var(--ln-text-primary, var(--text-primary));">${esc(cc)}</span>` : ''}
          </button>`;
      })
      .join('');

    results.innerHTML = `
      ${this.searching ? `<div style="font-size:12px;color:var(--ln-text-secondary, var(--text-secondary));">${esc(t('prayer_searching'))}</div>` : ''}
      ${items}
      ${!this.searching && this.searchResults.length === 0
        ? `<div style="font-size:12px;color:var(--ln-text-secondary, var(--text-secondary));padding:6px 2px;">${esc(t('prayer_search_hint'))}</div>`
        : ''}
    `;

    // Re-attach handlers on result buttons (they're freshly inserted)
    results.querySelectorAll<HTMLElement>('[data-act="add-result"]').forEach((el) => {
      el.addEventListener('click', (ev) => this.onAction('add-result', el, ev));
    });
  }

  // ---------- handlers ----------

  private attachHandlers(): void {
    if (!this.container) return;
    this.container.querySelectorAll('[data-act]').forEach((el) => {
      const act = el.getAttribute('data-act');
      if (!act) return;
      const tag = el.tagName.toLowerCase();
      const isInput = tag === 'input' || tag === 'select';
      const evtName = isInput ? 'input' : 'click';
      el.addEventListener(evtName, (ev) => this.onAction(act, ev.currentTarget as HTMLElement, ev));
      // For select/checked inputs we also want change events to fire on radio-button picks.
      if (tag === 'input') {
        el.addEventListener('change', (ev) => this.onAction(act, ev.currentTarget as HTMLElement, ev));
      }
    });
  }

  private onAction(act: string, el: HTMLElement, ev: Event): void {
    switch (act) {
      case 'pick-city': {
        ev.preventDefault();
        const id = el.getAttribute('data-id') || '';
        this.pickById(id);
        return;
      }
      case 'remove-city': {
        ev.preventDefault();
        ev.stopPropagation();
        const id = el.getAttribute('data-id') || '';
        this.watched.remove(id);
        if (this.active?.id === id) {
          const loc = this.locationService.getState();
          if (loc.lat !== null && loc.lng !== null) {
            this.setActive({
              id: 'current', lat: loc.lat, lng: loc.lng,
              name: loc.city || (this.ctx?.i18n.t('prayer_current_location') ?? 'Current'),
              isCurrent: true,
            });
          } else {
            this.active = null; this.activeData = null; this.render();
          }
        } else {
          this.render();
        }
        return;
      }
      case 'search-toggle':
        this.searchOpen = !this.searchOpen;
        this.searchResults = [];
        this.render();
        return;
      case 'add-result': {
        ev.preventDefault();
        const lat = parseFloat(el.getAttribute('data-lat') || '');
        const lng = parseFloat(el.getAttribute('data-lng') || '');
        const name = el.getAttribute('data-name') || '';
        const cc = el.getAttribute('data-cc') || undefined;
        const admin1 = el.getAttribute('data-admin1') || undefined;
        const tz = el.getAttribute('data-tz') || undefined;
        if (Number.isFinite(lat) && Number.isFinite(lng) && name) {
          const added = this.watched.add({ name, lat, lng, countryCode: cc, admin1, timeZone: tz });
          if (added) {
            this.searchOpen = false; this.searchResults = [];
            this.setActive({ id: added.id, lat: added.lat, lng: added.lng, name: added.name, isCurrent: false, tz });
          }
        }
        return;
      }
      case 'toggle-settings':
        this.settingsOpen = !this.settingsOpen;
        this.renderSettings();
        this.attachHandlers();
        return;
      case 'set-auto-method': {
        const enabled = (el as HTMLInputElement).checked;
        prayerTimeService.setAutoMethod(enabled);
        // Re-render settings so the toggle visual state + the disabled
        // style of method/madhab controls reflect the new value.
        this.refreshActiveData();
        this.renderSettings();
        this.attachHandlers();
        // Hero card / today list show the recomputed times.
        this.renderHero();
        this.renderTodayList();
        return;
      }
      case 'set-method': {
        const v = (el as HTMLSelectElement).value as CalculationMethodKey;
        prayerTimeService.setMethod(v);
        // setMethod turns auto OFF — re-render settings to reflect.
        this.refreshActiveData();
        this.renderSettings();
        this.attachHandlers();
        return;
      }
      case 'set-madhab': {
        const v = (el as HTMLInputElement).value as MadhabKey;
        if (v === 'Shafi' || v === 'Hanafi') {
          prayerTimeService.setMadhab(v);
          // setMadhab also turns auto OFF.
          this.refreshActiveData();
          this.renderSettings();
          this.attachHandlers();
        }
        return;
      }
      case 'set-highlat': {
        const v = (el as HTMLSelectElement).value as HighLatitudeRuleKey;
        prayerTimeService.setHighLatRule(v);
        return;
      }
      case 'set-midnight': {
        const v = (el as HTMLSelectElement).value as MidnightModeKey;
        prayerTimeService.setMidnightMode(v);
        return;
      }
      case 'set-shafaq': {
        const v = (el as HTMLSelectElement).value as ShafaqKey;
        prayerTimeService.setShafaq(v);
        return;
      }
      case 'set-tune': {
        const prayer = el.getAttribute('data-prayer') as keyof PrayerTuneConfig | null;
        const val = parseInt((el as HTMLInputElement).value, 10);
        if (prayer && Number.isFinite(val)) {
          prayerTimeService.setTune({ [prayer]: val } as Partial<PrayerTuneConfig>);
          const out = this.container?.querySelector(`[data-tune-val="${prayer}"]`) as HTMLElement | null;
          if (out) out.textContent = `${val >= 0 ? '+' : ''}${val} m`;
        }
        return;
      }
      case 'set-iqama': {
        const prayer = el.getAttribute('data-prayer') as keyof IqamaDelaysConfig | null;
        const val = parseInt((el as HTMLInputElement).value, 10);
        if (prayer && Number.isFinite(val)) {
          prayerTimeService.setIqamaDelays({ [prayer]: val } as Partial<IqamaDelaysConfig>);
          const out = this.container?.querySelector(`[data-iqama-val="${prayer}"]`) as HTMLElement | null;
          if (out) out.textContent = `+${val} m`;
          // Update aria-valuenow / valuetext so screen-reader users hear
          // the new value when they release the slider.
          el.setAttribute('aria-valuenow', String(val));
          el.setAttribute('aria-valuetext', `${val} ${this.ctx?.i18n.t('prayer_iqama_offset_min') || 'minutes'}`);
        }
        return;
      }
      case 'set-iqama-remind': {
        const enabled = (el as HTMLInputElement).checked;
        prayerTimeService.setIqamaReminderEnabled(enabled);
        return;
      }
      case 'set-force-local': {
        const enabled = (el as HTMLInputElement).checked;
        prayerTimeService.setForceLocalCalc(enabled);
        // Re-render to surface the new times immediately.
        this.renderHero();
        this.renderTodayList();
        return;
      }
      case 'set-qasr': {
        const enabled = (el as HTMLInputElement).checked;
        prayerTimeService.setQasrEnabled(enabled);
        // Re-render hero so the qasr banner appears/disappears.
        this.renderHero();
        return;
      }
      case 'set-hijri-offset': {
        const v = parseInt(el.getAttribute('data-value') || '0', 10);
        if (Number.isFinite(v)) {
          prayerTimeService.setHijriOffset(v);
          // Re-render the settings panel so the radio state updates,
          // and the hero's Hijri ribbon picks up the new offset.
          this.renderHero();
          this.renderSettings();
        }
        return;
      }
      case 'set-reminder': {
        const prayer = el.getAttribute('data-prayer') as PrayerKey | null;
        const val = parseInt((el as HTMLInputElement).value, 10);
        if (prayer && Number.isFinite(val)) {
          prayerTimeService.setReminderOffset(prayer, val);
          const out = this.container?.querySelector(`[data-reminder-val="${prayer}"]`) as HTMLElement | null;
          if (out) out.textContent = `${val} m`;
        }
        return;
      }
      case 'qibla-enable':
        ev.preventDefault();
        void this.startCompass();
        return;
      case 'qibla-disable':
        ev.preventDefault();
        this.stopCompass();
        return;
    }
  }

  private pickById(id: string): void {
    if (id === 'current') {
      const loc = this.locationService.getState();
      if (loc.lat !== null && loc.lng !== null) {
        this.setActive({
          id: 'current', lat: loc.lat, lng: loc.lng,
          name: loc.city || (this.ctx?.i18n.t('prayer_current_location') ?? 'Current'),
          isCurrent: true,
        });
      }
      return;
    }
    const c: WatchedCity | undefined = this.watched.list().find((x) => x.id === id);
    if (c) this.setActive({ id: c.id, lat: c.lat, lng: c.lng, name: c.name, isCurrent: false, tz: c.timeZone });
  }

  // ---------- compass ----------

  private async startCompass(): Promise<void> {
    if (typeof DeviceOrientationEvent === 'undefined') return;

    // iOS gesture-required permission flow
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const reqPerm = (DeviceOrientationEvent as any).requestPermission;
    if (typeof reqPerm === 'function') {
      try {
        const state = await reqPerm.call(DeviceOrientationEvent);
        if (state !== 'granted') return;
      } catch {
        return;
      }
    }

    this.compassListener = (e: DeviceOrientationEvent) => {
      // iOS exposes `webkitCompassHeading` (true north). Android Chrome uses `alpha` (magnetic).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ios = (e as any).webkitCompassHeading;
      if (typeof ios === 'number') {
        this.compassHeading = ios;
      } else if (typeof e.alpha === 'number') {
        this.compassHeading = (360 - e.alpha) % 360;
      }
      this.renderQibla();
    };
    window.addEventListener('deviceorientation', this.compassListener as EventListener, true);
    this.compassEnabled = true;
    this.renderQibla();
  }

  private stopCompass(): void {
    if (this.compassListener) {
      window.removeEventListener('deviceorientation', this.compassListener as EventListener, true);
      this.compassListener = null;
    }
    this.compassEnabled = false;
    if (this.container) this.renderQibla();
  }

  // ---------- formatting helpers ----------

  private formatCountdown(totalSeconds: number): string {
    if (totalSeconds <= 0) return '00:00';
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
    return `${String(m).padStart(2, '0')}m`;
  }

  private formatHijri(date: Date): { day: string; monthName: string; year: string } {
    const h = gregorianToHijri(date);
    return { day: String(h.day), monthName: h.monthName, year: String(h.year) };
  }

  private sourceIcon(src: PrayerTimesData['source']): string {
    switch (src) {
      case 'server': return '\u{1F4E1}';
      case 'offline-cache': return '\u{1F4F6}';
      case 'local-calc': return '\u{1F4F1}';
    }
  }

  private sourceLabel(src: PrayerTimesData['source']): string {
    if (!this.ctx) return '';
    const t = this.ctx.i18n.t.bind(this.ctx.i18n);
    switch (src) {
      case 'server': return t('prayer_source_server');
      case 'offline-cache': return t('prayer_source_offline');
      case 'local-calc': return t('prayer_source_local');
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers (file-local)
// ---------------------------------------------------------------------------

function esc(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

